import React, { useRef } from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import apps from "../data/apps.js";

const PHONE_LAYERS = 12;

const Phone = () => (
  <div className="phone">
    {Array.from({ length: PHONE_LAYERS }, (_, i) => (
      <div
        key={i}
        className="phone-layer"
        style={{ transform: `translateZ(${-i * 1.5}px)` }}
      />
    ))}
    <div className="phone-front">
      <div className="phone-island" />
      <div className="phone-time">9:41</div>
      <div className="phone-grid">
        {apps.map((app) => (
          <div className="phone-app" key={app.name}>
            <img src={app.image} alt="" className={`phone-icon phone-icon-${app.imageKind}`} />
            <span>{app.name}</span>
          </div>
        ))}
        <div className="phone-app">
          <div className="phone-icon phone-icon-next">+</div>
          <span>Next</span>
        </div>
      </div>
      <div className="phone-widget">
        <span className="phone-widget-label">Now building</span>
        <span className="phone-widget-value">The next one.</span>
        <div className="phone-widget-bar">
          <span />
        </div>
      </div>
      <div className="phone-dock">
        <span />
        <span />
        <span />
        <span />
      </div>
    </div>
  </div>
);

const Hero = () => {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springX = useSpring(mouseX, { stiffness: 80, damping: 18 });
  const springY = useSpring(mouseY, { stiffness: 80, damping: 18 });

  const scrollRotX = useTransform(scrollYProgress, [0, 1], [8, 55]);
  const scrollRotZ = useTransform(scrollYProgress, [0, 1], [-8, 12]);
  const rotateX = useTransform([scrollRotX, springY], ([s, m]) => s - m * 14);
  const rotateY = useTransform(springX, (m) => -18 + m * 22);
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 260]);
  const phoneScale = useTransform(scrollYProgress, [0, 1], [1, 0.75]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  const gridTilt = useTransform(scrollYProgress, [0, 1], [72, 82]);

  const onMouseMove = (e) => {
    if (reduceMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set((e.clientX - rect.left) / rect.width - 0.5);
    mouseY.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section className="hero" ref={ref} onMouseMove={onMouseMove}>
      <div className="hero-sky">
        <div className="hero-sun" />
        <motion.div className="hero-grid" style={{ rotateX: gridTilt, opacity: fade }} />
      </div>

      <div className="hero-inner">
        <motion.div className="hero-copy" style={{ y: textY, opacity: fade }}>
          <motion.p
            className="eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            <span className="dot" /> Independent mobile app publisher
          </motion.p>
          <h1 className="hero-title">
            {["We build", "apps that", "move."].map((line, i) => (
              <span className="line-mask" key={line}>
                <motion.span
                  className={i === 2 ? "accent" : undefined}
                  initial={{ y: "110%", rotateX: -60 }}
                  animate={{ y: 0, rotateX: 0 }}
                  transition={{ duration: 1, delay: 0.2 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            className="hero-sub"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Meck Interactive designs, launches and grows games and apps for iOS, Android and the
            web. Small team, sharp products.
          </motion.p>
          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
          >
            <a href="#apps" className="btn">
              See our apps <span aria-hidden="true">→</span>
            </a>
            <a href="#contact" className="btn btn-ghost">
              Publish with us
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-stage"
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.div
            className="hero-phone-wrap"
            style={{ rotateX, rotateY, rotateZ: scrollRotZ, y: phoneY, scale: phoneScale }}
          >
            <Phone />
            <div className="chip chip-a">iOS</div>
            <div className="chip chip-b">Android</div>
            <div className="chip chip-c">Web</div>
            <div className="phone-shadow" />
          </motion.div>
        </motion.div>
      </div>

      <motion.a href="#apps" className="scroll-cue" style={{ opacity: fade }}>
        <span>Scroll</span>
        <span className="scroll-line" />
      </motion.a>
    </section>
  );
};

export default Hero;
